<template>
  <div>
    <div v-if="hasPrescriptions" class="dosage-tab-div">
      <!-- Order Selection Tabs -->
      <div class="order-tabs-container">
        <div 
          v-for="(order, index) in ordersWithPrescriptions" 
          :key="order.id"
          :class="['order-tab', { 'active-tab': activeOrderIndex === index }]"
          @click="setActiveOrder(index)"
        >
          {{ getOrderTabName(order, index) }}
        </div>
      </div>
      
      <!-- Prescription Content for Selected Order -->
      <div v-if="activeOrder" class="prescription-content">
        <div v-for="monthData in prescriptionProgression" :key="monthData.month" class="dosage-month-div">
          <div class="text-block-1256">Month {{ monthData.month }}</div>
          <div class="dosage-flex-div">
            <div class="div-block-1986">              
              <div class="text-block-1258">Supply List:</div>
              <ul role="list" class="list-12">
                <li class="list-item-10">
                  <div class="text-block-1259"><span class="supply-list-span">1</span> Alcohol wipes</div>
                </li>
                <li class="list-item-10">
                  <div class="text-block-1259"><span class="supply-list-span">4</span> Subcutaneous Needles for Weekly Injections.</div>
                </li>
              </ul>
            </div>
            <div class="dosage-steps-div">
              <div class="step-header">
                <span class="text-span-275">{{ monthData.name }}</span>.
              </div>
              <div class="dosage-step-1-2-div">
                <div class="dosage-step-div">
                  <div class="step-header"> Weekly Injection</div>
                  <p class="paragraph-161">{{ monthData.directions }}</p>
                  <img loading="lazy" src="@/assets/images/image_SyringeLarge.svg" alt="" class="image-548">
                  <a v-if="checkCreated" href="http://www.prioritymeds.com/help-desk/how-to-reconstitute-your-peptides" class="button-124 w-button" target="_blank">Watch Tutorial Video</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <p>No prescription found</p>
      <div style="height:380px"></div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      appMainWeb: process.env.VUE_APP_HOMEPAGE,
      provider: 1, // Set default provider or get it from props/store
      activeOrderIndex: 0 // Track which order's prescription is currently displayed
    };
  },

  computed: {
    ...mapGetters(['getLatestPrescription', 'getOrdersWithPrescriptions', 'user']),
    
    checkCreated() {
      try {
        const createdAt = new Date(this.user.created_at);
        const comparisonDate = new Date("2025-04-09T20:33:52.956Z");
        return createdAt < comparisonDate;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    
    // Get orders that have prescriptions
    ordersWithPrescriptions() {
      return this.getOrdersWithPrescriptions;
    },
    
    // Check if any orders have prescriptions
    hasPrescriptions() {
      return this.ordersWithPrescriptions.length > 0;
    },
    
    // Get the currently active order
    activeOrder() {
      if (this.ordersWithPrescriptions.length === 0) return null;
      return this.ordersWithPrescriptions[this.activeOrderIndex];
    },
    
    // Get prescriptions for the active order only
    currentPrescriptions() {
      if (!this.activeOrder || !this.activeOrder.prescription_data) return [];
      return this.activeOrder.prescription_data;
    },
    
    // List of medication IDs to exclude (B12 and Ondansetron)
    excludedMedications() {
      return [
        // B12 IDs
        "6f9aadcb-c63f-436e-b601-41dfaef706a0",
        "f00adef7-02c0-4b4a-9b8b-bcdfd72eedd7",
        "9f1mGcnJDZRSMmheYLHqbQNmKh3uaUpO",
        // Ondansetron IDs
        "c900b781-8cd8-4dbe-8fc2-7c840fc191b9",
        "6cc657ff-1f9f-4394-9cab-ca4c803178e1",
        "4svB2w3PpV2Vyg4VCux3SfpdmiEXfvlu",
        //refill ids
         "efae95c2-7f01-4d6a-b50f-70142d39018a",
      "2179880a-52d0-4148-96b3-3ccf4548dc44",
      "7861bbf4-06d7-4f76-baba-f78809dd9279",
      "543f3035-b1d4-485b-97b9-db1ec0972d58",
      "382db5bb-fa90-43bb-bd33-bfe772340130",
      "04b6a9aa-63dc-4124-bddc-cbc9330eb77f"
      ];
    },
    
    // Medication mappings - YOU WILL FILL THIS IN with your existing mappings
    medicationMappings() {
      // ADD YOUR MEDICATION MAPPINGS HERE
      const semaglutideMap = {
        '3sCbH0Tf6VMTdf3ehUVDGt1y11wFpsOU': { sku: 'sema25', strength: 0.25 },
        '3Tem8fSmmCX1R3dMZmlXz160O3paFSrW': { sku: 'sema5', strength: 0.5 },
        'o6AaVChgdggsxu0YE81zygV0PTXSnGU1': { sku: 'sema1', strength: 1 },
        'I01wThBUkUGaOYiTqjGz79qCkJ6BRXqs': { sku: 'sema175', strength: 1.75 },
        'plAKt1r6PbpZ3FeJ81ZclUEuRSLAsf20': { sku: 'sema250', strength: 2.5 },
        "aaeb6153-c218-4d9d-acbd-5b5d5c68e096": { sku: "sema25", strength: 0.25 },
        "07759b93-c69f-42f8-99f3-061f3002ebe7": { sku: "sema25", strength: 0.25 },
        "11155c2c-c79e-425a-b66f-209001ca1c70": { sku: "sema25", strength: 0.25 },
        "07f55eca-1869-41d5-95ae-dfbebce74b94": { sku: "sema25", strength: 0.25 },
        "bf1884c2-57c1-4d11-bd92-6688b526c3cf": { sku: "sema25", strength: 0.25 },
        "11c58894-c7d3-4d6a-802c-d4a3e25ebd1e": { sku: "sema25", strength: 0.25 },
        "184f37f1-6ccc-417e-b928-2866a8bbac08": { sku: "sema5", strength: 0.5 },
        "60e4a828-ec73-40a4-8f74-161092837d66": { sku: "sema5", strength: 0.5 },
        "46cc2253-1dcd-4013-b759-07759acb48dc": { sku: "sema5", strength: 0.5 },
        "dc222b63-bac4-4e72-bbb3-ceb641723cd2": { sku: "sema5", strength: 0.5 },
        "23b448aa-ebbc-4fdd-a1b1-5e30c1ddbcd0": { sku: "sema5", strength: 0.5 },
        "ed8f50f9-c9b7-4b9a-8adc-3071b499f21a": { sku: "sema5", strength: 0.5 },
        "f23034a8-3be5-4678-a103-afaa02fbba42": { sku: "sema5", strength: 0.5 },
        "3e3a7678-eaab-4c3a-88c2-f96f6f5ce733": { sku: "sema1", strength: 1 },
        "fa1095fa-0cd5-40d2-9d00-db27f569a5bc": { sku: "sema1", strength: 1 },
        "2d9e7b61-b7d1-4db9-81b4-b31bc9a3b53e": { sku: "sema1", strength: 1 },
        "55effd60-3aa1-4a9f-bbaa-3e87267e36ce": { sku: "sema1", strength: 1 },
        "f790f185-08c2-4c15-9985-db6a4705ebc7": { sku: "sema1", strength: 1 },
        "bd5b3789-f5b5-407b-950b-49d03e872cb3": { sku: "sema1", strength: 1 },
        "62dbb14b-fdc8-495b-8fac-5033ab2377d2": { sku: "sema175", strength: 1.7 },
        "1adcde3e-e08c-4a05-9e78-1ad62e162391": { sku: "sema175", strength: 1.7 },
        "cbef945f-8d13-45d8-a745-2a8eea56f5fd": { sku: "sema175", strength: 1.7 },
        "b6409130-d4b4-424d-b1a0-66b398c9e634": { sku: "sema175", strength: 1.75 },
        "48c5efa6-6de3-4e76-a711-aa8765536eb1": { sku: "sema175", strength: 1.75 },
        "53f81f6e-e7c6-4bf9-a5ad-599449a95a9c": { sku: "sema175", strength: 1.75 },
        "686486bc-f40d-4ee4-9f86-4ef19657193b": { sku: "sema250", strength: 2.5 },
        "6abdd18a-f22d-408b-ad4f-5d4d9a616b59": { sku: "sema250", strength: 2.5 },
        "35ce6d66-4c21-49e9-93d5-cb2b43329b58": { sku: "sema250", strength: 2.5 },
        "907cbb72-974b-425f-986a-b3951ef6dd48": { sku: "sema250", strength: 2.5 },
        "24cb9ec2-fecb-41c8-bffc-98fea6c473c7": { sku: "sema250", strength: 2.5 },
        "acf6c703-5f40-427a-a6ed-d39ed6b903e8": { sku: "sema250", strength: 2.5 },
        "S1": { sku: "sema25", strength: 0.25 },
        "S2": { sku: "sema5", strength: 0.5 },
        "S3": { sku: "sema1", strength: 1 },
        "S4": { sku: "sema175", strength: 1.75 },
        "S5": { sku: "sema250", strength: 2.5 }
      };
      
      // Tirzepatide mappings based on provider
      const tirzepatideMap = {
        'toQWSL4KaccWLcLpp21ALT7v3xrJaCkN': { sku: 'tirz25', strength: 2.5 },
        'Lm2ccFeNyxanJy8bOwF2BAM1h7mvFBGG': { sku: 'tirz5', strength: 5 },
        '4kdffAp6hcxrQbROfOvkL2eNq4sRgUdb': { sku: 'tirz75', strength: 7.5 },
        'TMaex2l5t2U3AF8G3OV4gzI2ux1qA3ha': { sku: 'tirz10', strength: 10 },
        'HfZx7u34qxJjDNJI9Y15w0VoCtqcIRIp': { sku: 'tirz125', strength: 12.5 },
        'vRumqQM6QwgLu2bJpiWgpF7OS3O0AJ5U': { sku: 'tirz15', strength: 15 },
        'zPg1m7G9BLw5FTc0VMIfX7NF9fmjGAGX': { sku: 'tirz15', strength: 15 },
        "73d441be-4090-46be-9120-65ebc90baf7e": { sku: "tirz25", strength: 2.5 },
        "41892a03-1042-40d0-9419-f0b1073ef2c7": { sku: "tirz25", strength: 2.5 },
        "193ce31d-0dc7-437b-83ba-152940c31078": { sku: "tirz25", strength: 2.5 },
        "b70a3079-a458-46e9-b827-252ab3f2542b": { sku: "tirz25", strength: 2.5 },
        "16780c28-4a8f-44fa-bd80-1a5fa9182a55": { sku: "tirz25", strength: 2.5 },
        "4585e737-cf38-42d2-91cd-3e9608bbdb0a": { sku: "tirz25", strength: 2.5 },
        "a2ea0f55-a7ae-476d-a440-898ad77eb51c": { sku: "tirz5", strength: 5 },
        "98b39a25-f081-4046-9f91-238071f01730": { sku: "tirz5", strength: 5 },
        "d0c4d3b9-5799-4255-bd38-9414a0578408": { sku: "tirz5", strength: 5 },
        "23700042-7849-4b2f-a556-0531f8221843": { sku: "tirz5", strength: 5 },
        "15747320-ce78-4875-b1b0-7b9b49d42a28": { sku: "tirz5", strength: 5 },
        "b4cc23aa-180f-49d7-97c2-bad926b7ad5e": { sku: "tirz5", strength: 5 },
        "53d0a076-f5d9-48cc-8d06-a5a6eefc7e65": { sku: "tirz75", strength: 7.5 },
        "9555be41-3ecb-453d-ab0d-ca979c22ac48": { sku: "tirz75", strength: 7.5 },
        "4f66a742-9a88-485a-a894-f5eea5c652c4": { sku: "tirz75", strength: 7.5 },
        "9095db24-2bb3-4ff4-aaf4-60e299e20521": { sku: "tirz75", strength: 7.5 },
        "31256a95-9465-4488-87aa-24120b6a14cc": { sku: "tirz75", strength: 7.5 },
        "7737e018-b609-48af-b64a-071b1d877e1b": { sku: "tirz75", strength: 7.5 },
        "40aed8d9-ecee-4b50-8bde-2f831f5c4e86": { sku: "tirz10", strength: 10 },
        "973465c9-8dbf-4609-ae9a-c6730743785f": { sku: "tirz10", strength: 10 },
        "e380d258-c42c-4b88-9fc3-341e7a337d71": { sku: "tirz10", strength: 10 },
        "6dc41daf-d55d-4500-bb5f-e13fb97bb860": { sku: "tirz10", strength: 10 },
        "8cee1a80-90cf-4b97-9c4f-4c25441c4fd6": { sku: "tirz10", strength: 10 },
        "cfa5d6b4-696d-4c4d-b13f-07581112dc4e": { sku: "tirz10", strength: 10 },
        "33258e1f-64cb-41fd-ac3a-e4e78e99e1e7": { sku: "tirz125", strength: 12.5 },
        "4b3b3026-a7d8-4476-aa19-d123da01b1a1": { sku: "tirz125", strength: 12.5 },
        "df08fddd-8357-4d74-b2c3-c944dad1e712": { sku: "tirz125", strength: 12.5 },
        "96974e64-400c-46fd-ae06-f1fcd88bb2bb": { sku: "tirz125", strength: 12.5 },
        "b21765ea-7b34-412b-babc-c877c4f67433": { sku: "tirz125", strength: 12.5 },
        "62ebb335-0664-4941-b034-039ddccba2a6": { sku: "tirz125", strength: 12.5 },
        "1e8847b0-6016-435b-bc22-f60fd0eade89": { sku: "tirz15", strength: 15 },
        "2f939a62-5ee9-4abc-9716-260996b11411": { sku: "tirz15", strength: 15 },
        "65c4b74e-d383-45f2-ac1b-110bf113d607": { sku: "tirz15", strength: 15 },
        "3544e9dd-5b7c-48b7-8d6c-0122809962df": { sku: "tirz15", strength: 15 },
        "5e084ded-96c2-4887-8d9c-60b4f7fba072": { sku: "tirz15", strength: 15 },
        "32b18a45-88ed-40ae-acbc-af460bd8f48f": { sku: "tirz15", strength: 15 },
        "T1": { sku: "tirz1", strength: 2.5 },
        "T2": { sku: "tirz5", strength: 5 },
        "T3": { sku: "tirz75", strength: 7.5 },
        "T4": { sku: "tirz10", strength: 10 },
        "T5": { sku: "tirz125", strength: 12 },
        "T6": { sku: "tirz15", strength: 15 }
      };
      

      return {
        Semaglutide: semaglutideMap,
        Tirzepatide: tirzepatideMap
      };
    },
    
    // Use the direct generation method for prescription progression
    prescriptionProgression() {
      return this.generatePrescriptionProgression();
    }
  },

  methods: {
    // Set which order's prescription is displayed
    setActiveOrder(index) {
      if (index >= 0 && index < this.ordersWithPrescriptions.length) {
        this.activeOrderIndex = index;
      }
    },
    
    // Get a display name for the order tab
    getOrderTabName(order, index) {
      // Try to determine medication type from the order
      let medType = '';
      if (order.prescription_data && order.prescription_data.length > 0) {
        for (const med of order.prescription_data) {
          if (!med || !med.name) continue;
          
          const nameLower = med.name.toLowerCase();
          if (nameLower.includes('tirzepatide')) {
            medType = 'Tirzepatide';
            break;
          }
          
          if (nameLower.includes('semaglutide')) {
            medType = 'Semaglutide';
            break;
          }
        }
      }
      
      // Get date if available
      let dateStr = '';
      if (order.created_at) {
        const date = new Date(order.created_at);
        dateStr = date.toLocaleDateString();
      }
      
      // Construct tab name
      if (medType && dateStr) {
        return `${medType} (${dateStr})`;
      } else if (medType) {
        return medType;
      } else if (dateStr) {
        return `Order ${index + 1} (${dateStr})`;
      } else {
        return `Order ${index + 1}`;
      }
    },
    
    /**
     * Helper method to determine medication type for a specific prescription
     * PRIORITIZES the ID mapping to ensure correct medication type
     */
    getMedicationTypeForPrescription(prescription) {
      if (!prescription) return '';
      
      // FIRST: Check medication ID in mappings
      if (prescription.medId) {
        // Check Tirzepatide mappings
        if (this.medicationMappings.Tirzepatide && this.medicationMappings.Tirzepatide[prescription.medId]) {
          return 'Tirzepatide';
        }
        
        // Check Semaglutide mappings
        if (this.medicationMappings.Semaglutide && this.medicationMappings.Semaglutide[prescription.medId]) {
          return 'Semaglutide';
        }
      }
      
      // FALLBACK: Check name if ID mapping doesn't work
      if (prescription.name) {
        const nameLower = prescription.name.toLowerCase();
        if (nameLower.includes('semaglutide') || nameLower.includes('s1,') || 
            nameLower.includes('s2,') || nameLower.includes('s3,') || nameLower.includes('s4,')) {
          return 'Semaglutide';
        }
        if (nameLower.includes('tirzepatide')) {
          return 'Tirzepatide';
        }
      }
      
      return '';
    },

    /**
     * Updated method to get medication type for the entire order
     */
    getMedicationType() {
      if (!this.currentPrescriptions || this.currentPrescriptions.length === 0) {
        return '';
      }
      
      // Count occurrences of each type
      let semaglutideCount = 0;
      let tirzepatideCount = 0;
      
      for (const prescription of this.currentPrescriptions) {
        if (this.isExcludedMedication(prescription)) continue;
        
        const type = this.getMedicationTypeForPrescription(prescription);
        if (type === 'Semaglutide') semaglutideCount++;
        if (type === 'Tirzepatide') tirzepatideCount++;
      }
      
      // Return the most common type
      if (semaglutideCount > tirzepatideCount) return 'Semaglutide';
      if (tirzepatideCount > semaglutideCount) return 'Tirzepatide';
      
      // If tied or none found, default to Semaglutide (based on your sample data)
      return 'Semaglutide';
    },

    /**
     * Improved method to check if a medication should be excluded
     */
    isExcludedMedication(prescription) {
      if (!prescription) return false;
      
      // Check by medication ID first
      if (prescription.medId && this.excludedMedications.includes(prescription.medId)) {
        return true;
      }
      
      // Check by name
      if (prescription.name) {
        const name = prescription.name.toLowerCase();
        
        // Pure Ondansetron or B12/Cyanocobalamin
        if (name === 'ondansetron (oral - tablet)' || 
            name === 'ondansetron' ||
            name === 'cyanocobalamin' ||
            name === 'b12') {
          return true;
        }
        
        // Don't exclude combination products (e.g., "Semaglutide 1mg/250mcg B12")
        if ((name.includes('ondansetron') || 
             name.includes('cyanocobalamin') || 
             name.includes('b12')) && 
            !(name.includes('semaglutide') || name.includes('tirzepatide'))) {
          return true;
        }
      }
      
      return false;
    },
    
    /**
     * Enhanced method to extract dosage from prescription
     * PRIORITIZES the ID mapping to ensure exact strengths from your database
     */
    getDosageFromPrescription(prescription) {
      // Check if prescription exists
      if (!prescription) {
        return '0';
      }
      
      // FIRST AND FOREMOST: Check medication ID in mappings
      if (prescription.medId) {
        // Check in Tirzepatide mappings
        if (this.medicationMappings.Tirzepatide && this.medicationMappings.Tirzepatide[prescription.medId]) {
          const strength = this.medicationMappings.Tirzepatide[prescription.medId].strength;
          return strength;
        }
        
        // Check in Semaglutide mappings
        if (this.medicationMappings.Semaglutide && this.medicationMappings.Semaglutide[prescription.medId]) {
          const strength = this.medicationMappings.Semaglutide[prescription.medId].strength;
          return strength;
        }
      }
      
      // FALLBACK ONLY - use these methods if ID mapping fails
      
      // Try special handling for S1, S2, S3 prefixes (standardized progression)
      if (prescription.name) {
        if (prescription.name.toLowerCase().includes('s1,')) {
          return '0.25';
        }
        if (prescription.name.toLowerCase().includes('s2,')) {
          return '0.5';
        }
        if (prescription.name.toLowerCase().includes('s3,')) {
          return '1';
        }
        if (prescription.name.toLowerCase().includes('s4,')) {
          return '1.7';
        }
      }
      
      // Extract from directions - look for specific patterns
      if (prescription.directions) {
        // Pattern: "Inject X units (Ymg)"
        const injectMatch = prescription.directions.match(/Inject\s+\d+\s+units\s+\((\d+\.?\d*)mg\)/i);
        if (injectMatch) {
          return injectMatch[1];
        }
        
        // Pattern: (Xmg)
        const parenthesesMatch = prescription.directions.match(/\((\d+\.?\d*)mg\)/i);
        if (parenthesesMatch) {
          return parenthesesMatch[1];
        }
      }
      
      // Extract from name - for patterns like "S1, Semaglutide 1mg/250mcg B12 /ml , 0.25mg weekly injection"
      if (prescription.name) {
        // Pattern: extract the ending dose "X.XXmg weekly injection"
        const weeklyMatch = prescription.name.match(/,\s*(\d+\.?\d*)mg\s+weekly\s+injection/i);
        if (weeklyMatch) {
          return weeklyMatch[1];
        }
        
        // Try to extract first mentioned mg value (typically for main medication)
        const mgMatch = prescription.name.match(/(\d+\.?\d*)mg\/\d+mcg/i);
        if (mgMatch) {
          return mgMatch[1];
        }
      }
      
      // Extract from strength field
      if (prescription.strength) {
        const strengthMatch = prescription.strength.match(/(\d+\.?\d*)\s*mg/i);
        if (strengthMatch) {
          return strengthMatch[1];
        }
        
        // For combined strength format (e.g., "2mg/250mcg")
        const combinedMatch = prescription.strength.match(/(\d+\.?\d*)mg\/\d+mcg/i);
        if (combinedMatch) {
          return combinedMatch[1];
        }
      }
      
      // Fallback to 0 if no patterns match
      return '0';
    },

    /**
     * Format strength value for display
     */
    formatStrength(value) {
      if (value === undefined || value === null) return '0';
      
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return '0';
      
      if (Number.isInteger(numValue)) {
        return numValue.toString();
      }
      
      const decimalPlaces = numValue * 10 % 1 === 0 ? 1 : 2;
      return numValue.toFixed(decimalPlaces);
    },

    /**
     * Enhanced method to get filtered prescriptions
     */
    getFilteredPrescriptions() {
      if (!this.currentPrescriptions || this.currentPrescriptions.length === 0) {
        return [];
      }
      
      const medicationType = this.getMedicationType();
      if (!medicationType) {
        return [];
      }
      
      // Filter prescriptions based on type and exclusion rules
      return this.currentPrescriptions.filter(prescription => {
        // Skip excluded medications
        if (this.isExcludedMedication(prescription)) {
          return false;
        }
        
        // Include if it matches the medicine type
        const prescriptionType = this.getMedicationTypeForPrescription(prescription);
        return prescriptionType === medicationType;
      });
    },

    /**
     * Enhanced method to get sorted prescriptions
     */
    getSortedPrescriptions() {
      const filteredPrescriptions = this.getFilteredPrescriptions();
      if (!filteredPrescriptions.length) {
        return [];
      }
      
      // Sort by dosage
      return [...filteredPrescriptions].sort((a, b) => {
        const strengthA = parseFloat(this.getDosageFromPrescription(a)) || 0;
        const strengthB = parseFloat(this.getDosageFromPrescription(b)) || 0;
        return strengthA - strengthB;
      });
    },

    /**
     * Direct method to generate prescription progression
     */
    generatePrescriptionProgression() {
      const sortedPrescriptions = this.getSortedPrescriptions();
      if (!sortedPrescriptions.length) {
        return [];
      }
      
      const medicationType = this.getMedicationType();
      const result = [];
      
      // Build the progression
      for (let i = 0; i < sortedPrescriptions.length; i++) {
        const prescription = sortedPrescriptions[i];
        const rawStrength = this.getDosageFromPrescription(prescription);
        
        result.push({
          month: i + 1,
          strength: this.formatStrength(rawStrength),
          directions: prescription.directions,
          medicationType: medicationType,
          name: this.cleanMedicationName(prescription.name) || `${medicationType} ${this.formatStrength(rawStrength)}mg`
        });
      }
      
      return result;
    },
    
    /**
     * Clean medication name by removing T1-T6 and S1-S6 prefixes
     */
    cleanMedicationName(name) {
      if (!name) return '';
      
      // Remove T1-T6 or S1-S6 prefixes (with comma and optional space)
      return name.replace(/^[TS]\d+,\s*/, '').trim();
    },
    
    /**
     * Get the image URL for a medication
     */
    getImageUrl(medicationType, strength, isPoster = false) {
      try {
        // Handle strength conversion
        const numStrength = parseFloat(strength);
        const adjustedStrength = numStrength > 15 ? numStrength / 2 : numStrength;
        
        const baseUrl = 'https://prioritymeds.s3.us-east-1.amazonaws.com/public/assets/label_updated/';
        const suffix = isPoster ? '-p-500' : '';
        
        // Format the filename according to the pattern
        let formattedStrength = adjustedStrength;
        
        // Handle special case for numbers less than 1 (e.g., 0.25 -> .25)
        if (adjustedStrength < 1) {
          formattedStrength = `.${(adjustedStrength * 100)}`;
        }
        //let name = this.getImageName(formattedStrength);
        const filename = `${medicationType}-${formattedStrength}mg${suffix}.jpg`;
        return `${baseUrl}${filename}`;
      } catch (e) {
        console.error('Error loading image:', e);
        return '';
      }
    },
    
  }
}
</script>

<style scoped>
.order-tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  padding: 10px;
}

.order-tab {
  padding: 8px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-family: var(--mulish);
  font-weight: 600;
  transition: all 0.2s ease;
}

.order-tab:hover {
  background-color: #f5f5f5;
}

.order-tab.active-tab {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

.prescription-content {
  margin-top: 15px;
}

.medication-name {
  margin: 10px 0;
  padding: 8px;
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 4px;
  font-family: var(--mulish);
}
</style>